import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import VoiceRecorder from "@/components/VoiceRecorder";
import { RiskBadge } from "@/components/RiskBadge";
import { Loader2, AlertTriangle, ShieldAlert, CheckCircle2, ArrowRight } from "lucide-react";

const CONDITION_NAMES = {
  depression: "Depression", anxiety: "Anxiety", parkinsons: "Parkinson's", stroke: "Stroke",
  cognitive_ability: "Cognitive Ability", dementia: "Dementia",
  early_onset_dementia: "Early-Onset Dementia", alzheimers: "Alzheimer's",
};

export default function VoiceCapture() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("record"); // record | analyzing | rejected | done
  const [notes, setNotes] = useState([]);
  const [triage, setTriage] = useState(null);

  const consentOk = user.consent_status === "accepted";

  const handleComplete = async ({ blob, duration, avgAmplitude }) => {
    setPhase("analyzing");
    const fd = new FormData();
    fd.append("file", blob, "recording.webm");
    fd.append("duration_seconds", duration);
    fd.append("avg_amplitude", avgAmplitude);
    try {
      const { data } = await api.post("/voice/sessions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.quality_pass) {
        setNotes(data.quality_notes || ["Recording failed quality check."]);
        setPhase("rejected");
        return;
      }
      setTriage(data.triage);
      setPhase("done");
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      setPhase("record");
    }
  };

  if (!consentOk) {
    return (
      <div className="max-w-xl mx-auto rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="voice-consent-block">
        <ShieldAlert className="h-10 w-10 text-amber-600 mx-auto mb-3" />
        <h2 className="font-head text-xl font-semibold text-ink">Consent required</h2>
        <p className="text-ink-soft mt-1">You must accept the informed consent before recording your voice.</p>
        <button onClick={() => navigate("/patient/consent")} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-brand text-white px-5 py-2.5 font-semibold hover:bg-brand-hover transition-colors">
          Go to consent <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="eyebrow">Step · Voice screening</div>
      <h1 className="font-head text-3xl font-bold text-ink mt-1">Record your voice</h1>
      <p className="text-ink-soft mt-2">A short sample is analysed by the validated model. Poor recordings are rejected before analysis.</p>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-8">
        {phase === "record" && <VoiceRecorder onComplete={handleComplete} />}

        {phase === "analyzing" && (
          <div className="py-16 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-brand mx-auto" />
            <p className="mt-4 font-head font-semibold text-ink">Running quality check & AI screening…</p>
            <p className="text-sm text-ink-muted mt-1">Analysing acoustic markers across 8 conditions.</p>
          </div>
        )}

        {phase === "rejected" && (
          <div className="py-8 text-center" data-testid="quality-rejected">
            <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-7 w-7 text-red-600" />
            </div>
            <h3 className="mt-4 font-head text-xl font-semibold text-ink">Recording rejected</h3>
            <p className="text-ink-soft mt-1">We never send low-quality audio to the model. Please re-record:</p>
            <ul className="mt-4 inline-block text-left text-sm text-red-700 space-y-1">
              {notes.map((n, i) => <li key={i} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-red-500" />{n}</li>)}
            </ul>
            <div>
              <button onClick={() => setPhase("record")} data-testid="rerecord-btn"
                className="mt-6 rounded-lg bg-brand text-white px-6 py-2.5 font-semibold hover:bg-brand-hover transition-colors">
                Re-record
              </button>
            </div>
          </div>
        )}

        {phase === "done" && triage && (
          <div data-testid="triage-results">
            <div className="flex items-center gap-2 text-green-700 mb-5">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-head font-semibold">Triage generated across all conditions</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {triage.map((r) => (
                <div key={r.condition_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                  <span className="text-sm font-medium text-ink">{CONDITION_NAMES[r.condition_id]}</span>
                  <RiskBadge risk={r.risk} size="sm" />
                </div>
              ))}
            </div>
            <button onClick={() => navigate("/patient")} className="mt-6 w-full rounded-lg bg-brand text-white py-2.5 font-semibold hover:bg-brand-hover transition-colors">
              View my dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
